// Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms and the SOW between the parties dated 10/31/2024.

import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, input, Input, Output, ViewChild, ElementRef, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { UtilsPipe } from '../../utils.pipe';
import { MarkdownComponent } from 'ngx-markdown';

interface PromptDialog {
  uuid: string;
  type: 'prompt' | 'response';
  value: string;
}

@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [CommonModule, UtilsPipe, MarkdownComponent],
  templateUrl: './dialog.component.html',
  styleUrl: './dialog.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DialogComponent implements AfterViewInit, OnChanges {
  @Input() dialog: any;
  @Input() isLoading: boolean = false;
  @Input() isLast: boolean = false;
  @Input() isLastItem: boolean = false;
  @Input() errorWithWebSocket: boolean = false;
  @Input() errorResponseReceived: boolean = false;
  @Input() hasExcessiveDelay: boolean = false;
  @Output() clickEvent = new EventEmitter<string>();
  @Output() embeddedLinkClick = new EventEmitter<string>();
  
  @ViewChild('waitMessage2') waitMessage2: ElementRef | undefined;
  
  private messageTransitionTimer: any = null;

  constructor() { }

  /**
   * Check if content contains markdown formatting
   */
  hasMarkdownFormatting(content: string): boolean {
    if (!content) return false;
    
    // Check for common markdown patterns
    const markdownPatterns = [
      /\*\*.*?\*\*/,           // Bold **text**
      /\*.*?\*/,               // Italic *text*
      /_.*?_/,                 // Italic _text_
      /`.*?`/,                 // Inline code `code`
      /```[\s\S]*?```/,        // Code blocks
      /^#{1,6}\s/m,            // Headers # ## ###
      /^\s*[-*+]\s/m,          // Unordered lists
      /^\s*\d+\.\s/m,          // Ordered lists
      /\[.*?\]\(.*?\)/,        // Links [text](url)
      /!\[.*?\]\(.*?\)/,       // Images ![alt](url)
      /\|.*?\|/,               // Tables
      /^>\s/m                  // Blockquotes
    ];
    
    return markdownPatterns.some(pattern => pattern.test(content));
  }
  
  ngAfterViewInit() {
    this.setupMessageTransition();
  }
  
  ngOnChanges(changes: SimpleChanges) {
    // If hasExcessiveDelay changes to true, setup the message transition
    if (changes['hasExcessiveDelay'] && changes['hasExcessiveDelay'].currentValue === true) {
      // Use setTimeout to ensure DOM is updated before accessing the element
      setTimeout(() => {
        this.setupMessageTransition();
      }, 0);
    }
  }
  
  private setupMessageTransition() {
    // Clear any existing timer
    if (this.messageTransitionTimer) {
      clearTimeout(this.messageTransitionTimer);
    }
    
    // Set a new timer to add the 'show' class after 5 seconds (when the first message completes)
    if (this.hasExcessiveDelay && this.waitMessage2?.nativeElement) {
      this.messageTransitionTimer = setTimeout(() => {
        if (this.waitMessage2?.nativeElement) {
          this.waitMessage2.nativeElement.classList.add('show');
        }
      }, 5000); // Full 5 second animation time of the first message
    }
  }

  onButtonClicked(buttonText: string) {
    this.clickEvent.emit(buttonText);
  }

  onContentTitleClicked(dialog: PromptDialog & { value: { dashboardId: string } }) {
    this.embeddedLinkClick.emit(dialog.value.dashboardId);
  }
  onDashboardButtonClicked(dashboard: { title: string; dashboardId: string }) {
    this.embeddedLinkClick.emit(dashboard.dashboardId);
  }
}
